export interface StrapiData<T = Record<string, unknown>> {
  data?: T | T[]
  [key: string]: unknown
}

/**
 * Known locale prefixes that should be replaced
 */
const KNOWN_LOCALE_PREFIXES = ['/fi-en', '/fi-fi', '/fi-sv', '/sv-en', '/sv-sv', '/de-en', '/de-de', '/en', '/fi', '/de', '/sv']

/**
 * Extract the locale prefix from a URL
 */
const extractLocalePrefix = (url: string): string | null => {
  const localPrefix = KNOWN_LOCALE_PREFIXES.find((prefix) => url.startsWith(prefix) && (url.length === prefix.length || url[prefix.length] === '/'))

  return localPrefix || null
}

/**
 * Check if a URL is a relative internal link that should be localized
 */
const isRelativeInternalLink = (url: string): boolean => url.startsWith('/') && !url.startsWith('//') && !url.includes('://')

/**
 * Check if a URL is an excluded link that should NOT be localized
 */
const EXCLUDED_PATHS = ['/blog', '/company', '/page']

const isExcludedLink = (url: string): boolean => EXCLUDED_PATHS.some((path) => url.startsWith(path)
    || new RegExp(`^https?://(www\\.)?droppe\\.com(/[a-z]{2}(-[a-z]{2})?)?${path}(/|$)`, 'i').test(url))

/**
 * Localize a single URL by replacing or prepending the current locale
 * @param url - The URL to localize
 * @param locale - Current locale (e.g., 'fi-en', 'de-en')
 * @returns Localized URL
 */
export const localizeStrapiUrl = (url: string, locale: string): string => {
  if (!url || typeof url !== 'string') {
    return url
  }

  // Exclude certain links
  if (isExcludedLink(url)) {
    return url
  }
  // Localize droppe.com links (absolute or relative)
  const droppeDomainRegex = /^https?:\/\/(www\.)?droppe\.com(\/.*)?$/i

  if (droppeDomainRegex.test(url)) {
    // Extract the path part for localization
    const urlObj = new URL(url, 'https://www.droppe.com')
    const path = urlObj.pathname
    const existingPrefix = extractLocalePrefix(path)
    let newPath: string
    if (existingPrefix) {
      newPath = path.replace(existingPrefix, `/${locale}`)
    } else {
      newPath = `/${locale}${path}`
    }
    // Rebuild the URL with the new path
    return urlObj.origin + newPath + urlObj.search + urlObj.hash
  }

  // Localize relative internal links
  if (isRelativeInternalLink(url)) {
    const existingPrefix = extractLocalePrefix(url)
    if (existingPrefix) {
      return url.replace(existingPrefix, `/${locale}`)
    }
    return `/${locale}${url}`
  }

  return url
}

/**
 * Localize all links within rich text content
 * @param content - HTML content string
 * @param locale - Current locale (e.g., 'fi-en', 'de-en')
 * @returns Localized content string
 */
export const localizeStrapiContent = (content: string, locale: string): string => {
  if (!content || typeof content !== 'string') {
    return content
  }

  // Server-side regex-based link replacement
  // Handle both single and double quotes, and various whitespace patterns
  return content.replace(
    /href\s*=\s*["']([^"']*)["']/gi,
    (match, url) => {
      const localizedUrl = localizeStrapiUrl(url, locale)
      // Preserve the original quote style
      const quote = match.includes('"') ? '"' : "'"
      return `href=${quote}${localizedUrl}${quote}`
    },
  )
}

/**
 * Process Strapi attributes to localize all relevant content fields
 * @param attributes - Strapi attributes object
 * @param locale - Current locale (e.g., 'fi-en', 'de-en')
 * @returns Processed attributes object
 */
export const localizeStrapiAttributes = (
  attributes: Record<string, unknown>,
  locale: string,
): Record<string, unknown> => {
  if (!attributes || typeof attributes !== 'object') {
    return attributes
  }

  const processed: Record<string, unknown> = { ...attributes }

  // HTML content fields that should be processed
  const htmlContentFields = [
    'content',
    'description',
    'singleProductDescription',
    'supplierShopDescription',
    'productCatalogDescription',
    'categoryPageBottom',
    'metaDescription',
    'metaTitle',
    'structuredData',
  ]

  // Process HTML content fields
  htmlContentFields.forEach((field) => {
    if (typeof processed[field] === 'string') {
      processed[field] = localizeStrapiContent(processed[field] as string, locale)
    }
  })

  // Process URL fields (single URL strings)
  const urlFields = [
    'link',
    'url',
    'imageLink',
    'ctaLink',
  ]

  urlFields.forEach((field) => {
    if (typeof processed[field] === 'string') {
      processed[field] = localizeStrapiUrl(processed[field] as string, locale)
    }
  })

  // Process nested objects recursively
  Object.keys(processed).forEach((key) => {
    if (typeof processed[key] === 'object' && processed[key] !== null) {
      if (Array.isArray(processed[key])) {
        // Handle arrays - process each item but preserve array structure
        processed[key] = (processed[key] as unknown[]).map((item) => {
          if (typeof item === 'object' && item !== null) {
            return localizeStrapiAttributes(item as Record<string, unknown>, locale)
          }
          return item
        })
      } else {
        // Handle regular objects
        processed[key] = localizeStrapiAttributes(processed[key] as Record<string, unknown>, locale)
      }
    }
  })

  return processed
}

interface StrapiBaseItem {
  id: number
  attributes: Record<string, unknown>
}
interface StrapiBaseResponse {
  data?: StrapiBaseItem[] | StrapiBaseItem
  [key: string]: unknown
}

/**
 * Process entire Strapi data structure with localization
 * @param rawData - Raw Strapi data
 * @param locale - Current locale (e.g., 'fi-en', 'de-en')
 * @returns Processed Strapi data
 */
export const localizeStrapiData = <T extends StrapiBaseResponse>(
  rawData: T,
  locale: string,
): T => {
  if (!rawData || !rawData.data) {
    return rawData
  }
  const processedData = { ...rawData }
  if (Array.isArray(processedData.data)) {
    processedData.data = processedData.data.map((item) => {
      const processedItem = { ...item }
      if (processedItem && typeof processedItem === 'object'
        && 'attributes' in processedItem && processedItem.attributes) {
        processedItem.attributes = localizeStrapiAttributes(processedItem.attributes, locale)
      }
      return processedItem as typeof item
    })
  } else if (
    processedData.data
    && typeof processedData.data === 'object'
    && 'attributes' in processedData.data
    && processedData.data?.attributes
  ) {
    processedData.data.attributes = localizeStrapiAttributes(
      (processedData.data).attributes,
      locale,
    )
  }
  return processedData
}
